import { useState } from 'react';
import { Package, Search, X } from 'lucide-react';
import { ProductGridSelector, ProductGridItem } from './ProductGridSelector';

interface ProductPickerFieldProps {
  /** Currently selected product name (display value) */
  value: string;
  /** Called with the chosen product name (string) — keeps backward compat with text fields */
  onChange: (name: string) => void;
  /** Called with the full product object — optional, when consumer wants more than just the name */
  onPick?: (product: ProductGridItem) => void;
  placeholder?: string;
  size?: 'sm' | 'md';
  /** When true, also lets user free-type a custom value (e.g., for non-catalog requirements) */
  allowFreeText?: boolean;
  className?: string;
  id?: string;
}

/**
 * A "select-or-type" field that opens the polished ProductGridSelector
 * (categories → products grid/list, pinned, search) when clicked.
 *
 * Use this anywhere a user would otherwise type a product name into a free-text field
 * (lead modal, capture form, deal interest, etc.).
 */
export function ProductPickerField({
  value,
  onChange,
  onPick,
  placeholder = 'Select a product…',
  size = 'md',
  allowFreeText = true,
  className = '',
  id,
}: ProductPickerFieldProps) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);

  const sizes = {
    sm: 'px-2.5 py-1.5 text-xs',
    md: 'px-3 py-2 text-sm',
  };

  function handleSelect(p: ProductGridItem) {
    onChange(p.name);
    onPick?.(p);
    setEditing(false);
  }

  // Free-text edit mode
  if (editing && allowFreeText) {
    return (
      <div className={`relative ${className}`}>
        <input
          id={id}
          type="text"
          autoFocus
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setEditing(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Escape') setEditing(false);
          }}
          placeholder={placeholder}
          className={`w-full rounded-lg border border-[var(--ink-0)] bg-[var(--bg-input,var(--bg-surface))] ${sizes[size]} text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]`}
        />
      </div>
    );
  }

  return (
    <>
      <div className={`relative ${className}`}>
        <button
          id={id}
          type="button"
          onClick={() => setOpen(true)}
          className={`group flex w-full items-center justify-between gap-2 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-input,var(--bg-surface))] ${sizes[size]} text-left text-[var(--text-primary)] transition-all hover:border-[var(--ink-3)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]`}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <Package
              size={size === 'sm' ? 13 : 15}
              className="shrink-0 text-[var(--text-muted)] group-hover:text-[var(--saffron-600)]"
            />
            {value ? (
              <span className="truncate font-medium">{value}</span>
            ) : (
              <span className="text-[var(--text-muted)]">{placeholder}</span>
            )}
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {value && (
              <span
                role="button"
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange('');
                }}
                aria-label="Clear selected product"
                className="rounded p-0.5 text-[var(--text-muted)] hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-primary)]"
                title="Clear"
              >
                <X size={13} />
              </span>
            )}
            <Search
              size={size === 'sm' ? 13 : 15}
              className="text-[var(--text-muted)]"
            />
          </span>
        </button>
        {allowFreeText && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="absolute -bottom-5 right-0 text-[10px] text-[var(--text-muted)] hover:text-[var(--ink-0)] hover:underline"
            title="Type a custom value instead"
          >
            type custom
          </button>
        )}
      </div>

      <ProductGridSelector
        open={open}
        onClose={() => setOpen(false)}
        onSelect={handleSelect}
      />
    </>
  );
}
